import React, { useState, useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getProductOrderDetails, makeRefund, updateProductOrders } from "../../../service";
import { formatedOrderDetails, formatAddressWithApartment } from "../../organisms/orders-list/OrdersList.utils";
import {
  Button,
  Col,
  Dropdown,
  Flex,
  Input,
  Modal,
  notification,
  Row,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  MoreOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import { useResponsive } from "../../../hooks/useResponsive";
import { useGroupedOrderItems } from "../../../hooks/useGroupedOrderItems";
import {
  StyledButton,
  OrderSection,
  OrderItem,
  SectionTitle,
} from "../order-modals/OrderModal.styles";
import { colors } from "../../../styles/theme";
import { useTranslation } from "react-i18next";
import { useBranchContext } from "../../../contexts/branch-context";
import { currencySymbol } from "../../../utils/currency";

interface OrderDetailsModalProps {
  id: string;
  disabled?: boolean;
  hideFooter?: boolean;
  onClose?: () => void;
  status?: string;
  onEdit?: (orderData: any) => void;
}

export const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ id, disabled, hideFooter = false, onClose, status, onEdit }) => {
  const { t } = useTranslation();
  const { currentBranch } = useBranchContext();
  const curSymbol = currencySymbol(currentBranch?.currency);
  const [details, setDetails] = useState<any>();
  const [rawOrderData, setRawOrderData] = useState<any>(null);
  const role = Cookies.get("role") || "WORKER";
  void useResponsive();

  /**
   * Optymalizacja: Wykorzystanie wspólnego hooka do grupowania pozycji
   * Wpływ na wydajność: Eliminuje duplikację kodu i wykorzystuje memoizację
   */
  const groupedItems = useGroupedOrderItems(details?.cart || []);

  /**
   * Calculate total discount amount on frontend
   */
  const calculatedDiscount = useMemo(() => {
    const appliedCoupon = details?.header?.appliedCoupon;
    if (!appliedCoupon) return 0;

    const isSpecificProducts = appliedCoupon.scope === 'SPECIFIC_PRODUCTS';
    const affectedProductIds = appliedCoupon.affectedProductIds || [];

    // Calculate total price for affected items only
    let affectedItemsTotal = 0;
    groupedItems.forEach(group => {
      const productId = group[0]?.productId;
      const itemPrice = group[0]?.price * group[0]?.quantity;

      if (!isSpecificProducts || (productId && affectedProductIds.includes(productId))) {
        affectedItemsTotal += itemPrice;
      }
    });

    // Calculate discount based on coupon type
    if (affectedItemsTotal > 0) {
      if (appliedCoupon.type === 'PERCENTAGE') {
        return Math.round((affectedItemsTotal * appliedCoupon.value) / 100);
      } else {
        // FIXED_AMOUNT
        return Math.min(appliedCoupon.value, affectedItemsTotal);
      }
    }

    return 0;
  }, [details?.header?.appliedCoupon, groupedItems]);

  const { isLoading } = useQuery<any, Error>(
    [`/product-order-details`, id],
    () => getProductOrderDetails({ id: id }),
    {
      onSuccess(data) {
        setRawOrderData(data);
        setDetails(formatedOrderDetails(data));
      },
      enabled: !!id,
    }
  );

  const query = useQueryClient();

  const { mutateAsync, isLoading: mutateIsLoading } = useMutation(makeRefund, {
    onSuccess: () => {
      query.invalidateQueries([`/product-order-details`]);
      query.invalidateQueries([`/product-orders`]);
      notification.success({
        message: t("orderDetailsModal.refundSuccess"),
        style: {
          background: colors.successBackground,
        },
        placement: "bottomRight",
      });
      Modal.destroyAll();
    },
    onError: () => {
      notification.error({
        message: t("orderDetailsModal.refundError"),
        style: {
          background: colors.errorBackground,
        },
        placement: "bottomRight",
      });
      Modal.destroyAll();
    },
  });

  const {
    mutateAsync: updateOrderMutateAsync,
    isLoading: updateOrderIsLoading,
  } = useMutation(updateProductOrders, {
    onSuccess: () => {
      query.invalidateQueries({
        predicate: (queryItem) => {
          const key = queryItem.queryKey[0];
          return key === "product-orders" || key === "/product-orders";
        }
      });
      query.invalidateQueries([`/product-order-details`]);
      if (onClose) {
        onClose();
      }
    },
  });

  /**
   * Optymalizacja: Handlery z useCallback
   * Wpływ na wydajność: Stabilne referencje funkcji zapobiegają rerenderom
   */
  const handleFinish = useCallback(async () => {
    if (!id) return;

    await updateOrderMutateAsync({
      id: id,
      body: {
        status: "DONE",
      },
    });
  }, [id, updateOrderMutateAsync]);

  const handleMoveToDelivery = useCallback(async () => {
    if (!id) return;

    await updateOrderMutateAsync({
      id: id,
      body: {
        status: "IN_DELIVERY",
      },
    });
  }, [id, updateOrderMutateAsync]);

  const handleMoveBackToProgress = useCallback(async () => {
    if (!id) return;

    await updateOrderMutateAsync({
      id: id,
      body: {
        status: "IN_PROGRESS",
      },
    });
  }, [id, updateOrderMutateAsync]);

  if (!details && isLoading)
    return (
      <Flex
        style={{
          width: "100%",
          height: "200px",
        }}
        justify="center"
        align="center"
      >
        <Spin size="large" />
      </Flex>
    );

  if (details) {
    const isLoadingAction = updateOrderIsLoading || mutateIsLoading;

    return (
      <>
        {/* Custom Header with Title and Badge */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 600 }}>
                {t("orderDetailsModal.title")}
              </div>
              {rawOrderData?.isInternalOrder && onEdit && status !== "DONE" && (
                <Button
                  onClick={() => onEdit(rawOrderData)}
                  type="primary"
                >
                  {t("orderDetailsModal.edit")}
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Tag
                style={{
                  fontSize: 14,
                  padding: "6px 16px",
                  fontWeight: 500,
                  borderRadius: 6,
                  background: details.header.paymentsType === "OFFLINE" ? colors.warningLight :
                    details.header.paymentsType === "OFFLINE_PAID" ? colors.successLight :
                      colors.successLight,
                  borderColor: details.header.paymentsType === "OFFLINE" ? colors.warning :
                    details.header.paymentsType === "OFFLINE_PAID" ? colors.success :
                      colors.success,
                  color: details.header.paymentsType === "OFFLINE" ? colors.warning :
                    details.header.paymentsType === "OFFLINE_PAID" ? colors.success :
                      colors.success
                }}
              >
                {`${details.header.paymentsLabel} - ${((details.header.price) / 100).toFixed(2)} ${curSymbol}`}
              </Tag>
              {details.header.appliedCoupon && calculatedDiscount > 0 && (
                <Tag style={{ fontSize: 12, padding: "4px 12px", fontWeight: 500, borderRadius: 6, background: colors.successLight, borderColor: colors.success, color: colors.success }}>
                  {t("orderDetailsModal.couponTag", { code: details.header.appliedCoupon.code, amount: (calculatedDiscount / 100).toFixed(2) })}
                </Tag>
              )}
              {details.header.refund && (
                <Tag style={{ margin: 0, background: colors.errorLight, borderColor: colors.error, color: colors.error }}>{t("orderDetailsModal.refunded")}</Tag>
              )}
              {/* Quick payment status toggle for internal orders */}
              {rawOrderData?.isInternalOrder && status !== "DONE" &&
                (details.header.paymentsType === "OFFLINE" || details.header.paymentsType === "OFFLINE_PAID") && (
                  <Button
                    // size="small"
                    type={details.header.paymentsType === "OFFLINE" ? "primary" : "default"}
                    loading={updateOrderIsLoading}
                    onClick={async () => {
                      const newPaymentStatus = details.header.paymentsType === "OFFLINE" ? "OFFLINE_PAID" : "OFFLINE";
                      const newPaymentLabel = newPaymentStatus === "OFFLINE_PAID" ? t("orderDetailsModal.paymentCashCard") : t("orderDetailsModal.paymentNone");

                      await updateOrderMutateAsync({
                        id: id,
                        body: {
                          details: {
                            ...rawOrderData.details,
                            paymentsType: newPaymentStatus,
                            payments: newPaymentLabel,
                          },
                        },
                      });
                    }}
                  >
                    {details.header.paymentsType === "OFFLINE" ? t("orderDetailsModal.markAsPaid") : t("orderDetailsModal.markAsUnpaid")}
                  </Button>
                )}
              {role === "ADMIN" &&
                (details.header.paymentsType === "PRZELEWY24" || details.header.paymentsType === "STRIPE") &&
                !details.header.refund && (
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: "1",
                          label: (
                            <div
                              onClick={() =>
                                Modal.info({
                                  icon: null,
                                  destroyOnClose: true,
                                  content: (
                                    <Flex
                                      vertical
                                      style={{
                                        paddingBottom: "20px",
                                      }}
                                      justify="center"
                                      align="center"
                                    >
                                      <Typography.Title level={5}>
                                        {t("orderDetailsModal.enterPin")}
                                      </Typography.Title>
                                      <Input.OTP
                                        length={4}
                                        onChange={(e) => {
                                          if (e.length === 4) {
                                            mutateAsync({
                                              productOrderId: id,
                                              pin: e,
                                            });
                                          }
                                        }}
                                        inputMode="numeric"
                                      />
                                    </Flex>
                                  ),
                                  footer: (
                                    <Flex
                                      style={{
                                        width: "100%",
                                      }}
                                      justify="center"
                                      align="center"
                                    >
                                      {mutateIsLoading ? (
                                        <Spin size="small" />
                                      ) : (
                                        <></>
                                      )}
                                    </Flex>
                                  ),
                                  closable: true,
                                })
                              }
                            >
                              {t("orderDetailsModal.makeRefund")}
                            </div>
                          ),
                        },
                      ],
                    }}
                    placement="bottomRight"
                    arrow={{ pointAtCenter: true }}
                  >
                    <Button
                      disabled={!!disabled}
                      icon={<MoreOutlined />}
                      size="small"
                    />
                  </Dropdown>
                )}
            </div>
          </div>

          {/* Date */}
          <Typography.Text style={{ fontSize: 13, color: colors.gray500 }}>
            {dayjs(details.header.createdAt).format("DD-MM-YYYY HH:mm")}
          </Typography.Text>
        </div>

        {/*
          Optymalizacja responsywności: Tablet (md) używa single column, desktop (lg) używa two columns
          Wpływ: Lepsze UX na tabletach z rozdzielczością 768-1024px
        */}
        <Row gutter={16}>
          {/* Left Column: Order Items */}
          <Col xs={24} md={24} lg={12}>
            {/* Komentarz klienta */}
            {details.footer.deliveryNote && (
              <>
                <SectionTitle level={5}>{t("orderDetailsModal.commentTitle")}</SectionTitle>
                <OrderSection style={{ background: "#fffbe6", borderColor: "#ffe58f" }}>
                  <Typography.Text style={{ fontSize: 14, color: "#614700" }}>
                    {details.footer.deliveryNote}
                  </Typography.Text>
                </OrderSection>
              </>
            )}

            {/* Order Items */}
            <SectionTitle level={5}>{t("orderDetailsModal.orderedItems")}</SectionTitle>
            <OrderSection>
              {(() => {
                // Calculate discount on frontend
                const appliedCoupon = details.header.appliedCoupon;
                const isSpecificProducts = appliedCoupon?.scope === 'SPECIFIC_PRODUCTS';
                const affectedProductIds = appliedCoupon?.affectedProductIds || [];

                // Calculate total price for affected items only
                let affectedItemsTotal = 0;
                groupedItems.forEach(group => {
                  const productId = group[0]?.productId;
                  const itemPrice = group[0]?.price * group[0]?.quantity;

                  // If specific products coupon, only count affected products
                  // If cart-wide coupon, count all products
                  if (!isSpecificProducts || (productId && affectedProductIds.includes(productId))) {
                    affectedItemsTotal += itemPrice;
                  }
                });

                // Calculate total discount amount based on coupon type
                let totalDiscountAmount = 0;
                if (appliedCoupon && affectedItemsTotal > 0) {
                  if (appliedCoupon.type === 'PERCENTAGE') {
                    totalDiscountAmount = Math.round((affectedItemsTotal * appliedCoupon.value) / 100);
                  } else {
                    // FIXED_AMOUNT
                    totalDiscountAmount = Math.min(appliedCoupon.value, affectedItemsTotal);
                  }
                }

                const itemElements = groupedItems.map((group, groupIndex) => {
                  // Get original price for this item
                  const itemOriginalPrice = group[0]?.price * group[0]?.quantity;
                  const productId = group[0]?.productId;
                  let itemDiscountedPrice = itemOriginalPrice;
                  let isProductAffected = false;

                  if (appliedCoupon && itemOriginalPrice && affectedItemsTotal > 0 && totalDiscountAmount > 0) {
                    // Check if this product is affected by the coupon
                    isProductAffected = !isSpecificProducts || (productId && affectedProductIds.includes(productId));

                    if (isProductAffected) {
                      // Calculate proportional discount for this item based on affected items total
                      const itemRatio = itemOriginalPrice / affectedItemsTotal;
                      const itemDiscount = Math.round(totalDiscountAmount * itemRatio);
                      itemDiscountedPrice = itemOriginalPrice - itemDiscount;
                    }
                  }

                  return (
                    <OrderItem key={groupIndex}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, flex: 1 }}>
                          {group.map((row: any, rowIndex: number) => (
                            <div key={rowIndex} style={{ ...row.style }}>
                              {row.texts.map((text: any, textIndex: number) => {
                                const displayText = text.text.replace(/\(1x\)\s*/g, '');

                                return (
                                  <span
                                    key={textIndex}
                                    style={{
                                      ...text.style,
                                      fontSize: 14,
                                    }}
                                  >
                                    {displayText}
                                  </span>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                        {/* Price display for the first item in group (product) */}
                        {group[0]?.price !== undefined && (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: '100px' }}>
                            {appliedCoupon && isProductAffected ? (
                              <>
                                <Typography.Text
                                  delete
                                  type="secondary"
                                  style={{ fontSize: 12 }}
                                >
                                  {(itemOriginalPrice / 100).toFixed(2)} {curSymbol}
                                </Typography.Text>
                                <Typography.Text
                                  strong
                                  style={{ fontSize: 14, color: colors.success }}
                                >
                                  {(itemDiscountedPrice / 100).toFixed(2)} {curSymbol}
                                </Typography.Text>
                              </>
                            ) : (
                              <Typography.Text style={{ fontSize: 14, fontWeight: 500 }}>
                                {(itemOriginalPrice / 100).toFixed(2)} {curSymbol}
                              </Typography.Text>
                            )}
                          </div>
                        )}
                      </div>
                    </OrderItem>
                  );
                });

                return (
                  <>
                    {itemElements}
                    {/* Show coupon discount summary if applicable */}
                    {appliedCoupon && totalDiscountAmount > 0 && (
                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--color-borderLight)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography.Text style={{ fontSize: 13, color: colors.success }}>
                            {t("orderDetailsModal.appliedCoupon", { code: appliedCoupon.code })}
                          </Typography.Text>
                          <Typography.Text style={{ fontSize: 13, color: 'var(--color-success)', fontWeight: 500 }}>
                            -{(totalDiscountAmount / 100).toFixed(2)} {curSymbol}
                          </Typography.Text>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </OrderSection>
          </Col>

          {/* Right Column: Payment, Delivery Info and Contact Details */}
          <Col xs={24} md={24} lg={12}>
            {/* Payment Info */}
            <SectionTitle level={5}>{t("orderDetailsModal.paymentTitle")}</SectionTitle>
            <OrderSection>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                    {t("orderDetailsModal.method")}
                  </Typography.Text>
                  <Typography.Text style={{ fontSize: 14, fontWeight: 500 }}>
                    {details.header.paymentsLabel}
                  </Typography.Text>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                    {t("orderDetailsModal.status")}
                  </Typography.Text>
                  <Tag
                    color={
                      details.header.paymentsType === "OFFLINE"
                        ? "gold"
                        : details.header.refund
                        ? "red"
                        : "green"
                    }
                    style={{ margin: 0 }}
                  >
                    {details.header.paymentsType === "OFFLINE"
                      ? t("orderDetailsModal.statusAwaitingPayment")
                      : details.header.refund
                      ? t("orderDetailsModal.statusRefunded")
                      : (details.header.paymentsType === "PRZELEWY24" || details.header.paymentsType === "STRIPE")
                      ? t("orderDetailsModal.statusPaidOnline")
                      : t("orderDetailsModal.statusPaid")}
                  </Tag>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                    {t("orderDetailsModal.amount")}
                  </Typography.Text>
                  <Typography.Text style={{ fontSize: 14, fontWeight: 500 }}>
                    {(details.header.price / 100).toFixed(2)} {curSymbol}
                  </Typography.Text>
                </div>
                {details.header.appliedCoupon && calculatedDiscount > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                      {t("orderDetailsModal.couponLabel", { code: details.header.appliedCoupon.code })}
                    </Typography.Text>
                    <Typography.Text style={{ fontSize: 14, fontWeight: 500, color: colors.success }}>
                      -{(calculatedDiscount / 100).toFixed(2)} {curSymbol}
                    </Typography.Text>
                  </div>
                )}
              </div>
            </OrderSection>

            {/* Delivery Info */}
            <SectionTitle level={5}>{t("orderDetailsModal.deliveryDataTitle")}</SectionTitle>
            <OrderSection>
              <div>
                <Typography.Text style={{ fontSize: 14, display: "block", marginBottom: 4 }}>
                  {details.footer.deliveryType === "SELF_PICKUP"
                    ? t("orderDetailsModal.selfPickup")
                    : formatAddressWithApartment(details.footer.deliveryLabel, details.footer.apertmenNumber)}
                </Typography.Text>
                {details.footer.tableName && (
                  <Typography.Text strong style={{ fontSize: 14, display: "block", marginBottom: 4, color: colors.primary }}>
                    {t("orderDetailsModal.table", { name: details.footer.tableName })}
                  </Typography.Text>
                )}
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  {details.footer.deliveryTimeType === "NOW"
                    ? t("orderDetailsModal.asSoonAsPossible")
                    : t("orderDetailsModal.withDate", { date: details.footer.deliveryOrderDate })}
                </Typography.Text>
              </div>
            </OrderSection>

            {/* Contact Details */}
            <SectionTitle level={5}>{t("orderDetailsModal.customerDataTitle")}</SectionTitle>
            <OrderSection>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {details.address.slice(1).map((row: any, index: number) => (
                  <div key={index}>
                    {row.texts.map((text: any, textIndex: number) => (
                      <span
                        key={textIndex}
                        style={{
                          ...text.style,
                          fontSize: 14,
                        }}
                      >
                        {text.text}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </OrderSection>

          </Col>
        </Row>

        {/* Action Buttons - only show if not hideFooter */}
        {!hideFooter && (
          <div style={{ display: 'flex', gap: '12px', marginTop: 24 }}>
            {status === "IN_DELIVERY" ? (
              <>
                <StyledButton
                  className="gray-button"
                  onClick={handleMoveBackToProgress}
                  loading={isLoadingAction}
                  disabled={isLoadingAction}
                >
                  {t("orderDetailsModal.backToPreparation")}
                </StyledButton>
                <StyledButton
                  className="primary-button"
                  onClick={handleFinish}
                  loading={isLoadingAction}
                  disabled={isLoadingAction}
                >
                  {t("orderDetailsModal.deliveredFinish")}
                </StyledButton>
              </>
            ) : (
              <>
                <StyledButton
                  className="finish-button"
                  onClick={handleFinish}
                  loading={isLoadingAction}
                  disabled={isLoadingAction}
                >
                  {t("orderDetailsModal.finish")}
                </StyledButton>
                <StyledButton
                  className="primary-button"
                  onClick={handleMoveToDelivery}
                  loading={isLoadingAction}
                  disabled={isLoadingAction}
                >
                  {t("orderDetailsModal.moveToDelivery")}
                </StyledButton>
              </>
            )}
          </div>
        )}

        {/* Order ID */}
        <div style={{ marginTop: 16, textAlign: 'center', fontSize: 13, color: colors.gray500 }}>
          {t("orderDetailsModal.orderId", { id })}
        </div>
      </>
    );
  }

  return null;
};
